import React, {useContext, useEffect, useState} from "react";
import {Mui, MuiIcons, MuiLab} from "@leap4/frontend";
import {getClientForOrganization, postNewClient, regenerateClient} from "../../../../actions/users";
import {AppDispatch} from "../../../../store/store";
import {OrganizationContext} from "../OrganizationContex";
import {IClient} from "./IClient";

const {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} = Mui;
const {ContentCopy: ContentCopyIcon} = MuiIcons;
const {LoadingButton} = MuiLab;

interface ClientFormProps {
    organizationId: string;
    dispatch: AppDispatch;
}

const MASKED_SECRET = "*".repeat(50);

export const ClientForm: React.FC<ClientFormProps> = ({organizationId, dispatch}) => {
    const [clientKey, setClientKey] = useState("");
    const [secretKey, setSecretKey] = useState("");
    const [isGenerated, setIsGenerated] = useState(false);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const {organization, setClientCredentials} = useContext(OrganizationContext);

    useEffect(() => {
        if (!clientKey || !secretKey) {
            setClientCredentials(null);
            return;
        }

        setClientCredentials({
            clientKey,
            visibleSecret: secretKey !== MASKED_SECRET ? secretKey : null,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientKey, secretKey]);

    useEffect(() => {
        let isActive = true;

        setClientKey("");
        setSecretKey("");
        setIsGenerated(false);
        setError("");
        setCopied(false);
        setConfirmOpen(false);
        setIsRegenerating(false);
        setClientCredentials(null);

        if (!organizationId) {
            return () => {
                isActive = false;
            };
        }

        const fetchClient = async () => {
            try {
                const existingClient = await getClientForOrganization(dispatch, organizationId) as IClient;
                if (!isActive) return;

                if (existingClient?.clientKey) {
                    setClientKey(existingClient.clientKey);
                    setSecretKey(MASKED_SECRET);
                    setIsGenerated(true);
                }
            } catch (err) {
                if (isActive) console.error("Failed to fetch client:", err);
            }
        };

        fetchClient();

        return () => {
            isActive = false;
        };
    }, [organizationId, dispatch, setClientCredentials]);

    const handleGenerateClient = async () => {
        if (!organizationId) return;
        setError("");

        const res = await postNewClient(dispatch, {
            clientKey,
            allowedSupplyPartnerIds: getAllowedSupplyPartnerIds(),
            organizationId,
        });

        if (res?.secretKey) {
            setSecretKey(res.secretKey);
            setIsGenerated(true);
        } else {
            setError("Client already exists or failed to generate");
        }
    };

    const handleRegenerateClient = async () => {
        if (!organizationId) return;
        setIsRegenerating(true);

        try {
            const res = await regenerateClient(dispatch, organizationId);
            if (res?.secretKey) {
                setClientKey(res.clientKey);
                setSecretKey(res.secretKey);
            }
        } finally {
            setIsRegenerating(false);
            setConfirmOpen(false);
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(secretKey);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            setError("Couldn't copy secret key");
        }
    };

    const getAllowedSupplyPartnerIds = () => {
        const partnerIds: string[] = [];
        Object.values(organization.publisherLogin || {}).forEach((login) => {
            partnerIds.push(...(login?.partners || []));
        });

        return Array.from(new Set(partnerIds.filter(partnerId => !!partnerId)));
    };

    return (
        <>
            <Typography variant="h5" mb={2}>Client Setup</Typography>

            <TextField
                fullWidth
                label="Client Key"
                value={clientKey}
                onChange={(e) => setClientKey(e.target.value)}
                disabled={isGenerated}
                margin="normal"
            />

            <Box display="flex" alignItems="center" gap={1}>
                <TextField
                    fullWidth
                    label="Secret Key"
                    value={secretKey}
                    disabled
                    margin="normal"
                    helperText={
                        secretKey !== MASKED_SECRET && !!secretKey
                            ? "This will be shown only once. Save it securely."
                            : ""
                    }
                />
                {secretKey !== MASKED_SECRET && !!secretKey && (
                    <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                        <IconButton onClick={handleCopy}>
                            <ContentCopyIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {error && (
                <Typography color="error" variant="body2" mt={1}>
                    {error}
                </Typography>
            )}

            {!isGenerated && (
                <Box mt={2}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleGenerateClient}
                        disabled={!clientKey}
                    >
                        Generate Client
                    </Button>
                </Box>
            )}

            {isGenerated && (
                <Box mt={2}>
                    <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => setConfirmOpen(true)}
                        disabled={isRegenerating}
                    >
                        Regenerate Client
                    </Button>
                </Box>
            )}

            <Dialog open={confirmOpen} onClose={() => !isRegenerating && setConfirmOpen(false)}>
                <DialogTitle>Regenerate client secret?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This immediately invalidates the existing secret. Any partner integration
                        using the old secret will stop working until it is updated. The new secret
                        will be shown only once.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        onClick={() => setConfirmOpen(false)}
                        loading={isRegenerating}
                    >
                        Cancel
                    </LoadingButton>
                    <LoadingButton
                        onClick={handleRegenerateClient}
                        color="warning"
                        variant="contained"
                        loading={isRegenerating}
                    >
                        Regenerate
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
};
